/**
 * Per-answer influence map — notes §§6-13 + runtime spec matrices.
 * Compilers import and apply; influence is constraint/prior, not deterministic plot.
 */
import { WIZARD_QUESTION_IDS } from '../constants.js';
import type { ActorContext, PromptPurpose, WorldContext } from './types.js';

/** Downstream dimensions an accepted answer may steer. */
export type DownstreamUse =
  | 'tone'
  | 'tech'
  | 'norms'
  | 'triggers'
  | 'topics'
  | 'events'
  | 'relationships';

/** Stable answer families mapped from wizard + runtime context. */
export type AnswerFamily =
  | 'location_name'
  | 'region'
  | 'language'
  | 'location_type'
  | 'description'
  | 'crowd'
  | 'rhythm'
  | 'time_period'
  | 'period_rationale'
  | 'schedule'
  | 'schedule_tz'
  | 'overnight'
  | 'appearance'
  | 'nature'
  | 'traits'
  | 'species'
  | 'sex'
  | 'age'
  | 'goals'
  | 'relationships'
  | 'clock'
  | 'recent_events'
  | 'summaries';

export interface InfluenceEntry {
  family: AnswerFamily;
  /** Wizard question ids or character field keys tied to this family. */
  sourceKeys: readonly string[];
  downstreamUses: readonly DownstreamUse[];
  eventInfluence: string;
  conversationInfluence: string;
  guardrail: string;
}

/** Canonical matrix — every family maps to ≥1 downstream use with an explicit guardrail. */
export const INFLUENCE_MATRIX: readonly InfluenceEntry[] = [
  {
    family: 'location_name',
    sourceKeys: [WIZARD_QUESTION_IDS.groundingName],
    downstreamUses: ['tone', 'topics', 'events', 'relationships'],
    eventInfluence: 'Anchor events to the accepted place name; no second venue.',
    conversationInfluence: 'Characters may reference the named place naturally.',
    guardrail: 'Do not invent a different location or unsupported landmark.',
  },
  {
    family: 'region',
    sourceKeys: [WIZARD_QUESTION_IDS.groundingRegion],
    downstreamUses: ['tone', 'tech', 'norms', 'topics', 'events'],
    eventInfluence: 'Weather, transit, civic, and seasonal events stay region-plausible.',
    conversationInfluence: 'Grounded local references; dialect register without stereotype.',
    guardrail: 'Avoid stereotypes; preserve unknowns and translation uncertainty.',
  },
  {
    family: 'language',
    sourceKeys: [WIZARD_QUESTION_IDS.groundingLanguage],
    downstreamUses: ['norms', 'topics', 'triggers', 'relationships'],
    eventInfluence: 'Signage, messages, and misunderstanding events use configured language.',
    conversationInfluence: 'Dialogue language, honorifics, and idioms follow accepted language.',
    guardrail: 'Never switch dialogue language without an accepted multilingual basis.',
  },
  {
    family: 'location_type',
    sourceKeys: [WIZARD_QUESTION_IDS.connectedLocationType],
    downstreamUses: ['tone', 'tech', 'norms', 'events', 'relationships'],
    eventInfluence: 'Events follow institution/household/business access patterns.',
    conversationInfluence: 'Role-appropriate topics and boundaries for the setting type.',
    guardrail: 'Do not use a generic scene that violates the accepted location type.',
  },
  {
    family: 'description',
    sourceKeys: [WIZARD_QUESTION_IDS.connectedDescription],
    downstreamUses: ['tone', 'tech', 'topics', 'triggers', 'events'],
    eventInfluence: 'Events may involve described spaces, hazards, routines, or resources.',
    conversationInfluence: 'Shared observations and place-specific subjects from description.',
    guardrail: 'Do not invent a second location or features absent from accepted description.',
  },
  {
    family: 'crowd',
    sourceKeys: [WIZARD_QUESTION_IDS.connectedCrowd],
    downstreamUses: ['tone', 'norms', 'triggers', 'topics', 'events', 'relationships'],
    eventInfluence: 'Queues, privacy loss, encounters, or quiet periods match crowd pattern.',
    conversationInfluence: 'Public, whispered, group, or private topics per crowd density.',
    guardrail: 'Do not create crowd events during a configured empty period.',
  },
  {
    family: 'rhythm',
    sourceKeys: [WIZARD_QUESTION_IDS.connectedRhythm],
    downstreamUses: ['tone', 'triggers', 'topics', 'events', 'relationships'],
    eventInfluence: 'Transition events cluster at openings, breaks, rushes, and closings.',
    conversationInfluence: 'Routine, fatigue, anticipation, and schedule talk stay salient.',
    guardrail: 'Rhythm raises likelihood; it does not guarantee an event.',
  },
  {
    family: 'time_period',
    sourceKeys: [WIZARD_QUESTION_IDS.periodPresent, WIZARD_QUESTION_IDS.periodPeriod],
    downstreamUses: ['tone', 'tech', 'norms', 'topics', 'events'],
    eventInfluence: 'Period-valid objects, institutions, and risks only.',
    conversationInfluence: 'Period-appropriate references and knowledge boundaries.',
    guardrail: 'Never import anachronistic technology without accepted fictional basis.',
  },
  {
    family: 'period_rationale',
    sourceKeys: [WIZARD_QUESTION_IDS.periodRationale],
    downstreamUses: ['tone', 'topics', 'events', 'relationships'],
    eventInfluence: 'Thematic events weighted toward the observer’s stated purpose.',
    conversationInfluence: 'Recurring themes tied to why the period was chosen.',
    guardrail: 'Rationale steers tone/themes; it is not a factual claim override.',
  },
  {
    family: 'schedule',
    sourceKeys: ['schedule.daysOpen', 'schedule.openTime', 'schedule.closeTime', 'schedule.closedBehavior'],
    downstreamUses: ['tone', 'triggers', 'topics', 'events', 'relationships'],
    eventInfluence: 'Activity allowed only while live; opening/closing transition triggers.',
    conversationInfluence: 'Opening plans, lateness, closing reflections, next availability.',
    guardrail: 'Closed state forbids new events and conversations.',
  },
  {
    family: 'schedule_tz',
    sourceKeys: ['schedule.timezoneStrategy', 'schedule.timezoneId'],
    downstreamUses: ['events', 'triggers', 'topics'],
    eventInfluence: 'Day-boundary and quota reset use the configured timezone.',
    conversationInfluence: 'Local-time references align with world clock interpretation.',
    guardrail: 'Do not compute live/closed from the wrong timezone.',
  },
  {
    family: 'overnight',
    sourceKeys: ['schedule.overnight'],
    downstreamUses: ['tone', 'tech', 'norms', 'triggers', 'topics', 'events'],
    eventInfluence: 'Overnight transitions without treating midnight as automatic closure.',
    conversationInfluence: 'Sleep, shift handoff, quiet, and darkness as relevant topics.',
    guardrail: 'Overnight flag changes day-boundary math; do not ignore it.',
  },
  {
    family: 'appearance',
    sourceKeys: ['character.appearance'],
    downstreamUses: ['tone', 'topics', 'relationships'],
    eventInfluence: 'Visual staging and palette only; no hidden canonical facts.',
    conversationInfluence: 'Characters may notice observable appearance when relevant.',
    guardrail: 'Visual preference cannot create hidden world facts or diagnose identity.',
  },
  {
    family: 'nature',
    sourceKeys: ['character.nature'],
    downstreamUses: ['tone', 'norms', 'triggers', 'topics', 'events', 'relationships'],
    eventInfluence: 'Initiation, persistence, retreat, escalation, or repair tendencies.',
    conversationInfluence: 'Disclosure, humor, interruption, and disagreement follow temperament.',
    guardrail: 'Nature is probabilistic; must not erase explicit character choices.',
  },
  {
    family: 'traits',
    sourceKeys: ['character.traits'],
    downstreamUses: ['tone', 'topics', 'triggers', 'events', 'relationships'],
    eventInfluence: 'Habits, expertise, rituals, or boundaries may surface in events.',
    conversationInfluence: 'Topics draw on knowledge, hobbies, routines, and preferences.',
    guardrail: 'Traits cannot grant impossible knowledge or override period/location constraints.',
  },
  {
    family: 'species',
    sourceKeys: ['character.species'],
    downstreamUses: ['tone', 'tech', 'norms', 'events', 'relationships'],
    eventInfluence: 'Movement, access, perception, and care events stay species-plausible.',
    conversationInfluence: 'Identity, movement, care, and perception topics when relevant.',
    guardrail: 'Species constrains affordances; do not invent unsupported capabilities.',
  },
  {
    family: 'sex',
    sourceKeys: ['character.sex'],
    downstreamUses: ['norms', 'topics', 'relationships'],
    eventInfluence: 'Historically grounded constraints only; never standalone device trigger.',
    conversationInfluence: 'Bounded social context where period/setting makes it relevant.',
    guardrail: 'Never stereotype; never assign personality or destiny from sex alone.',
  },
  {
    family: 'age',
    sourceKeys: ['character.ageRange'],
    downstreamUses: ['tone', 'tech', 'norms', 'topics', 'events', 'relationships'],
    eventInfluence: 'School, work, care, fatigue, and milestone likelihood by life stage.',
    conversationInfluence: 'Register, authority, references, and generational contrast.',
    guardrail: 'Age filters triggers; it does not deterministically control behavior.',
  },
  {
    family: 'goals',
    sourceKeys: ['character.goals'],
    downstreamUses: ['tone', 'topics', 'triggers', 'events', 'relationships'],
    eventInfluence: 'Events may advance, obstruct, expose, or complicate a goal.',
    conversationInfluence: 'Planning, negotiation, confession, or avoidance around goals.',
    guardrail: 'Goals are context, not guaranteed plot outcomes.',
  },
  {
    family: 'relationships',
    sourceKeys: ['character.relationships'],
    downstreamUses: ['tone', 'norms', 'triggers', 'topics', 'events', 'relationships'],
    eventInfluence: 'Contact, friction, support, repair, distance, or trust change.',
    conversationInfluence: 'Same fact phrased per directional relationship state.',
    guardrail: 'Relationship changes require persisted event effect with bounded deltas.',
  },
  {
    family: 'clock',
    sourceKeys: ['simulation.clock'],
    downstreamUses: ['tone', 'tech', 'triggers', 'topics', 'events', 'relationships'],
    eventInfluence: 'Time-valid events; enforce daily quota and eligibility windows.',
    conversationInfluence: 'Urgency, greetings, and temporal references grounded in now.',
    guardrail: 'Do not generate activity after closure or outside eligibility window.',
  },
  {
    family: 'recent_events',
    sourceKeys: ['history.recentEvents'],
    downstreamUses: ['tone', 'topics', 'events', 'relationships'],
    eventInfluence: 'Causal follow-up and consequence likelihood from recent continuity.',
    conversationInfluence: 'Characters know only what they could observe or retain.',
    guardrail: 'Do not repeat or contradict recent persisted events.',
  },
  {
    family: 'summaries',
    sourceKeys: ['history.summaries'],
    downstreamUses: ['tone', 'topics', 'events', 'relationships'],
    eventInfluence: 'Long-arc events without loading unbounded transcript.',
    conversationInfluence: 'Compact older facts and unresolved threads when recent history thin.',
    guardrail: 'Summaries are evidence, not permission to fabricate omitted details.',
  },
] as const;

/** Purposes and the answer families they must consult. */
const PURPOSE_FAMILIES: Record<PromptPurpose, readonly AnswerFamily[]> = {
  connected_questions: [
    'location_name',
    'region',
    'language',
    'location_type',
    'description',
    'crowd',
    'rhythm',
    'time_period',
    'period_rationale',
  ],
  assumption: [
    'location_name',
    'region',
    'language',
    'location_type',
    'description',
    'crowd',
    'rhythm',
    'time_period',
    'period_rationale',
    'schedule',
    'schedule_tz',
    'overnight',
  ],
  world_devices: [
    'location_name',
    'region',
    'language',
    'location_type',
    'description',
    'crowd',
    'rhythm',
    'time_period',
    'period_rationale',
    'schedule',
    'overnight',
  ],
  character_options: [
    'location_name',
    'region',
    'language',
    'location_type',
    'description',
    'time_period',
    'period_rationale',
    'appearance',
    'nature',
    'traits',
    'species',
    'sex',
    'age',
  ],
  runtime_scene: [
    'location_name',
    'region',
    'language',
    'location_type',
    'description',
    'crowd',
    'rhythm',
    'time_period',
    'period_rationale',
    'schedule',
    'schedule_tz',
    'overnight',
    'appearance',
    'nature',
    'traits',
    'species',
    'sex',
    'age',
    'goals',
    'relationships',
    'clock',
    'recent_events',
    'summaries',
  ],
  location_summary: ['schedule', 'schedule_tz', 'overnight', 'clock', 'recent_events', 'summaries'],
  missed_activity: [
    'schedule',
    'schedule_tz',
    'overnight',
    'clock',
    'recent_events',
    'summaries',
  ],
};

const FAMILY_INDEX = new Map(INFLUENCE_MATRIX.map((e) => [e.family, e]));

/** Structural families always included when the purpose references them. */
const STRUCTURAL_FAMILIES = new Set<AnswerFamily>([
  'schedule',
  'schedule_tz',
  'overnight',
  'clock',
  'recent_events',
  'summaries',
]);

function isFamilyActive(family: AnswerFamily, world: WorldContext, actors?: ActorContext[]): boolean {
  if (STRUCTURAL_FAMILIES.has(family)) return true;

  switch (family) {
    case 'location_name':
      return Boolean(world.location.name?.trim());
    case 'region':
      return Boolean(world.location.region?.trim());
    case 'language':
      return Boolean(world.location.language?.trim());
    case 'location_type':
      return Boolean(world.connected?.locationType?.trim() || world.locationType);
    case 'description':
      return Boolean(world.connected?.description?.trim());
    case 'crowd':
      return Boolean(world.connected?.crowd);
    case 'rhythm':
      return Boolean(world.connected?.rhythm && !world.connected.rhythm.skipped);
    case 'time_period':
      return Boolean(world.timePeriod.present?.trim() || world.timePeriod.period?.trim());
    case 'period_rationale':
      return Boolean(world.timePeriod.rationale?.trim());
    case 'appearance':
    case 'nature':
      return true;
    case 'traits':
      return actors?.some((a) => (a.traits?.length ?? 0) > 0) ?? false;
    case 'species':
      return actors?.some((a) => Boolean(a.species)) ?? false;
    case 'sex':
      return actors?.some((a) => Boolean(a.sex)) ?? false;
    case 'age':
      return actors?.some((a) => Boolean(a.ageRange)) ?? false;
    case 'goals':
      return actors?.some((a) => (a.goals?.length ?? 0) > 0) ?? false;
    case 'relationships':
      return actors?.some((a) => (a.relationships?.length ?? 0) > 0) ?? false;
    default:
      return entrySourcePresent(family, world);
  }
}

function entrySourcePresent(family: AnswerFamily, world: WorldContext): boolean {
  const entry = FAMILY_INDEX.get(family);
  if (!entry) return false;
  return entry.sourceKeys.some((k) => k in world.answers);
}

function activeFamiliesForPurpose(
  purpose: PromptPurpose,
  world: WorldContext,
  actors?: ActorContext[],
): InfluenceEntry[] {
  const wanted = PURPOSE_FAMILIES[purpose];
  const entries: InfluenceEntry[] = [];

  for (const family of wanted) {
    const entry = FAMILY_INDEX.get(family);
    if (!entry) continue;
    if (!isFamilyActive(family, world, actors)) continue;
    entries.push(entry);
  }

  return entries;
}

export function influenceSourceIds(entries: readonly InfluenceEntry[], world: WorldContext): string[] {
  const ids = new Set<string>();
  for (const entry of entries) {
    for (const key of entry.sourceKeys) {
      if (key in world.answers) ids.add(key);
    }
  }
  return [...ids].sort();
}

export interface InfluenceBlock {
  text: string;
  families: AnswerFamily[];
  guardrails: string[];
  sourceIds: string[];
}

/** Build influence instructions compilers append to tier context. */
export function buildInfluenceBlock(
  purpose: PromptPurpose,
  world: WorldContext,
  actors?: ActorContext[],
): InfluenceBlock {
  const entries = activeFamiliesForPurpose(purpose, world, actors);
  const families = entries.map((e) => e.family);
  const guardrails = [...new Set(entries.map((e) => e.guardrail))];
  const sourceIds = influenceSourceIds(entries, world);

  if (entries.length === 0) {
    return { text: 'INFLUENCE: (no accepted answers for this purpose)', families, guardrails, sourceIds };
  }

  const lines = entries.map((e) => {
    const uses = e.downstreamUses.join(',');
    return `- ${e.family} → [${uses}] events: ${e.eventInfluence} | dialogue: ${e.conversationInfluence}`;
  });

  const guardBlock = guardrails.map((g) => `- ${g}`).join('\n');

  const text = [
    'INFLUENCE_MATRIX (accepted answers constrain output; stale excluded):',
    ...lines,
    'GUARDRAILS:',
    guardBlock,
    'DURABLE_FACT_RULE: major facts become proposed/persisted events — never untracked prose mutation.',
  ].join('\n');

  return { text, families, guardrails, sourceIds };
}

/** Operation-contract snippets shared by compilers. */
export const OPERATION_GUARDRAILS: Record<PromptPurpose, string> = {
  connected_questions: `CONTRACT: ≤3 dependency-ordered questions; approved controls only; never repeat/contradict accepted answers; nonessential questions require allowsNotSure: true and a "not sure" option.`,
  assumption: `CONTRACT: one primary + ≤2 alternatives for ONE field; preserve all confirmed answers; do not overwrite accepted values.`,
  world_devices: `CONTRACT: exactly 20 unique deviceIds post-review; each device needs category, trigger, eligibleActorIds, consequenceRange, intensity, contextSourceIds.`,
  character_options: `CONTRACT: editable appearance/trait options; preserve accepted fields; no sensitive identity inference or diagnosis.`,
  runtime_scene: `CONTRACT: structured JSON only — dialogue, eventCandidates, actorEffects, summaryText are separate; 2-5 minute conversation bound; summaryText/prose never authoritative; durable facts via eventCandidates (proposed:true).`,
  location_summary: `CONTRACT: persisted events only; mobile-short observedFacts; closed location → no new activity; separate facts from interpretation.`,
  missed_activity: `CONTRACT: persisted events only; closed → summarize stored activity, no missed-generation; mobile-short output.`,
};

/** Every answer family must appear in the matrix with ≥1 downstream use — test hook. */
export function allAnswerFamiliesCovered(): AnswerFamily[] {
  return INFLUENCE_MATRIX.map((e) => e.family);
}
